import { Drawer, Spin, Empty, Tag, Avatar, Button, Typography, Space } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  CalendarOutlined, EnvironmentOutlined, BankOutlined,
  CheckCircleOutlined, ClockCircleOutlined, MedicineBoxOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { registrationApi } from '../../../api/registration.api';
import { usePermission } from '../../../hooks/usePermission';

const { Text } = Typography;

const avatarColor = (name) => {
  const palette = ['var(--wl-primary-light)','var(--wl-primary)','#00897b','#1e88e5','#e53935','#fb8c00','#43a047','#6d4c41'];
  return palette[(name?.charCodeAt(0) || 65) % palette.length];
};

const ChildEventsDrawer = ({ open, onClose, child }) => {
  const navigate   = useNavigate();
  const { isAdmin, isDoctor } = usePermission();
  const canCheckup = isAdmin || isDoctor;

  const childId = child?.id || child?._id;

  const { data, isLoading } = useQuery({
    queryKey: ['child-registrations', childId],
    queryFn:  () => registrationApi.getByChild(childId),
    enabled:  open && !!childId,
  });

  const registrations = data?.data || [];

  return (
    <Drawer
      title={
        child ? (
          <Space>
            <Avatar size={38} style={{ background: avatarColor(child.name), fontWeight: 700, flexShrink: 0 }}>
              {child.name?.charAt(0)?.toUpperCase()}
            </Avatar>
            <div style={{ lineHeight: 1.3 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--wl-text)' }}>{child.name}</div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {[child.school?.name, child.childCode ? `ID: ${child.childCode}` : null]
                  .filter(Boolean).join(' · ')}
              </Text>
            </div>
          </Space>
        ) : 'Child Events'
      }
      width={460}
      open={open}
      onClose={onClose}
      destroyOnClose
      styles={{ body: { padding: '16px 20px', background: '#faf9ff' } }}
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
      ) : registrations.length === 0 ? (
        <Empty
          description={<Text type="secondary">No events found for this child</Text>}
          style={{ padding: '48px 0' }}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Text style={{ fontSize: 12, color: '#999' }}>
            {registrations.length} event{registrations.length !== 1 ? 's' : ''} registered
          </Text>

          {registrations.map((reg) => {
            const event  = reg.event || {};
            const eventId = event.id || event._id;
            const regId   = reg.id   || reg._id;
            const date    = event.eventDate ? dayjs(event.eventDate) : null;

            return (
              <div
                key={regId}
                style={{
                  border:       reg.isChecked ? '1.5px solid #b7eb8f' : '1.5px solid var(--wl-primary-pale)',
                  borderRadius: 14,
                  padding:      '14px 16px',
                  background:   reg.isChecked ? '#f6ffed' : '#ffffff',
                  boxShadow:    '0 2px 10px rgba(var(--wl-primary-rgb),0.07)',
                }}
              >
                {/* Title + status */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 8 }}>
                  <Text strong style={{ fontSize: 14, color: 'var(--wl-text)', flex: 1 }}>
                    {event.title || '—'}
                  </Text>
                  {reg.isChecked ? (
                    <Tag
                      icon={<CheckCircleOutlined />}
                      style={{ borderRadius: 9999, background: '#f0fff4', color: '#52c41a', border: '1px solid #b7eb8f', fontWeight: 600, fontSize: 11, flexShrink: 0 }}
                    >
                      Checked
                    </Tag>
                  ) : (
                    <Tag
                      icon={<ClockCircleOutlined />}
                      style={{ borderRadius: 9999, background: '#fff7e6', color: '#fa8c16', border: '1px solid #ffd591', fontWeight: 600, fontSize: 11, flexShrink: 0 }}
                    >
                      Awaiting
                    </Tag>
                  )}
                </div>

                {/* Meta */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: canCheckup ? 12 : 0 }}>
                  {date && (
                    <Text style={{ fontSize: 12, color: '#666' }}>
                      <CalendarOutlined style={{ color: 'var(--wl-primary)', marginRight: 5 }} />
                      {date.format('DD MMM YYYY')}
                    </Text>
                  )}
                  {event.location && (
                    <Text style={{ fontSize: 12, color: '#666' }}>
                      <EnvironmentOutlined style={{ color: 'var(--wl-primary)', marginRight: 5 }} />
                      {event.location}
                    </Text>
                  )}
                  {event.school?.name && (
                    <Text style={{ fontSize: 12, color: '#666' }}>
                      <BankOutlined style={{ color: 'var(--wl-primary)', marginRight: 5 }} />
                      {event.school.name}
                    </Text>
                  )}
                </div>

                {canCheckup && (
                  <Button
                    type="primary"
                    size="small"
                    icon={<MedicineBoxOutlined />}
                    style={{
                      background:    reg.isChecked
                        ? 'linear-gradient(135deg, #52c41a, #389e0d)'
                        : 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))',
                      border:        'none',
                      borderRadius:  9999,
                      fontWeight:    600,
                      fontSize:      12,
                      height:        30,
                      paddingLeft:   14,
                      paddingRight:  14,
                    }}
                    onClick={() => {
                      onClose();
                      navigate(`/events/${eventId}/children/${regId}?from=child`);
                    }}
                  >
                    {reg.isChecked ? 'View Checkup' : 'View Details'}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Drawer>
  );
};

export default ChildEventsDrawer;
